import { useState, useMemo, useEffect } from "react"
import { Select, Popconfirm } from "antd"
import { CloseCircleOutlined } from "@ant-design/icons"
import "../assets/styles/FloatingSelect.css"
import { useTranslation } from "react-i18next"

const { Option } = Select

/**
 * A select dropdown component with a floating label.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - The label text
 * @param {string|Array} props.value - The selected value(s)
 * @param {Function} props.onChange - Function called when selection changes
 * @param {Array} props.options - Array of options for the select
 * @param {string} props.error - Error message
 * @param {boolean} props.touched - Whether the field has been touched
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.fullWidth - Whether the select should take full width
 * @param {boolean} props.allowCustomOption - Whether to allow custom options
 * @param {string} props.mode - Select mode (e.g., "multiple")
 * @param {Function} props.onDeleteOption - Function called when an option is deleted
 * @returns {JSX.Element} The FloatingSelect component
 */
const FloatingSelect = ({
  label,
  value,
  onChange,
  options,
  error: propError,
  touched,
  required,
  fullWidth,
  allowCustomOption = false,
  mode,
  onDeleteOption,
  ...props
}) => {
  const { t } = useTranslation()
  const [isFocused, setIsFocused] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [error, setError] = useState(propError)

  useEffect(() => {
    setError(propError)
  }, [propError])

  const filteredOptions = useMemo(() => {
    return options
      .filter((option) => {
        const optionLabel = option.label || option.value || ""
        return typeof optionLabel === "string" && optionLabel.toLowerCase().includes(searchValue.toLowerCase())
      })
      .sort((a, b) => {
        const labelA = (a.label || a.value || "").toString().toLowerCase()
        const labelB = (b.label || b.value || "").toString().toLowerCase()
        const aStartsWith = labelA.startsWith(searchValue.toLowerCase())
        const bStartsWith = labelB.startsWith(searchValue.toLowerCase())

        if (aStartsWith && !bStartsWith) return -1
        if (!aStartsWith && bStartsWith) return 1
        return labelA.localeCompare(labelB)
      })
  }, [options, searchValue])

  /**
   * Handles search input changes.
   *
   * @param {string} value - The search input value
   */
  const handleSearch = (value) => {
    setSearchValue(value)
  }

  /**
   * Handles changes to the selected value.
   *
   * @param {string|Array} newValue - The new selected value(s)
   */
  const handleChange = (newValue) => {
    setError(false)
    onChange(newValue)
    setSearchValue("")
  }

  /**
   * Handles the blur event for the select component.
   * Adds custom option if applicable.
   */
  const handleBlur = () => {
    setIsFocused(false)
    if (
      mode === "multiple" &&
      allowCustomOption &&
      searchValue &&
      !options.find((opt) => (opt.value || "").toString().toLowerCase() === searchValue.toLowerCase())
    ) {
      onChange([...(Array.isArray(value) ? value : []), searchValue])
      setSearchValue("")
    }
  }

  /**
   * Handles keydown events for the select component.
   * Adds custom option on Enter if applicable.
   *
   * @param {Event} event - The keydown event
   */
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && searchValue && filteredOptions.length === 0 && allowCustomOption) {
      event.preventDefault()
      if (mode === "multiple") {
        onChange([...(value || []), searchValue])
      } else {
        onChange(searchValue)
      }
      setSearchValue("")
    }
  }

  /**
   * Clears the selected value(s).
   */
  const handleClear = () => {
    onChange(mode === "multiple" ? [] : undefined)
    setSearchValue("")
  }

  /**
   * Handles deletion of an option.
   *
   * @param {string} optionValue - The value of the option to delete
   */
  const handleDeleteOption = (optionValue) => {
    if (mode === "multiple") {
      const newValue = Array.isArray(value) ? value.filter((v) => v !== optionValue) : []
      onChange(newValue)
    } else if (value === optionValue) {
      onChange(undefined)
    }
    if (onDeleteOption) {
      onDeleteOption(optionValue)
    }
  }

  /**
   * Renders an option with delete button if applicable.
   *
   * @param {Object} option - The option to render
   * @returns {JSX.Element} The rendered option
   */
  const renderOption = (option) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      {option.label || option.value}
      {onDeleteOption && (
        <Popconfirm
          title={t("floatingSelect.confirmDelete")}
          onConfirm={(e) => {
            e.stopPropagation()
            handleDeleteOption(option.value)
          }}
          okText={t("floatingSelect.yes")}
          cancelText={t("floatingSelect.no")}
        >
          <CloseCircleOutlined style={{ color: "red", marginLeft: "10px" }} onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      )}
    </div>
  )

  return (
    <div
      className={`floating-select ${isFocused || (value && (Array.isArray(value) ? value.length > 0 : value)) ? "focused" : ""} ${error ? "error" : ""} ${required ? "required" : ""} ${fullWidth ? "full-width" : ""} ${mode === "multiple" ? "multiple" : ""}`}
    >
      <Select
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        showSearch
        onSearch={handleSearch}
        filterOption={false}
        placeholder=" "
        onKeyDown={handleKeyDown}
        allowClear={true}
        onClear={handleClear}
        mode={mode}
        {...props}
      >
        {filteredOptions.map((option) => (
          <Option key={option.value} value={option.value}>
            {renderOption(option)}
          </Option>
        ))}
        {allowCustomOption &&
          mode === "multiple" &&
          searchValue &&
          !filteredOptions.find(
            (opt) => (opt.label || opt.value || "").toString().toLowerCase() === searchValue.toLowerCase(),
          ) && (
            <Option value={searchValue} key={searchValue}>
              {t("modalContent.add")} "{searchValue}"
            </Option>
          )}
      </Select>
      <label>
        {label}
        {required && "*"}
      </label>
    </div>
  )
}

export default FloatingSelect